import React, { useEffect, useRef } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import appPreview from "../assets/3.png";
import webLogo from "../assets/logo.png";
// Store badges from src/assets (lokalizované soubory SVG)
import gpCs from "../assets/android/cs.svg";
import gpDe from "../assets/android/de.svg";
import gpEn from "../assets/android/en.svg";
import gpEs from "../assets/android/es.svg";
import iosCsBlack from "../assets/ios/cs_black.svg";
import iosDeBlack from "../assets/ios/de_black.svg";
import iosEnBlack from "../assets/ios/en_black.svg";
import iosEsBlack from "../assets/ios/es_black.svg";

const Download: React.FC = () => {
   const { t, i18n } = useTranslation();

   // Map current language to available badge locales
   const rawLang = (i18n.language || "en").toLowerCase();
   const lang = ["cs", "de", "en", "es"].includes(rawLang.slice(0, 2))
      ? (rawLang.slice(0, 2) as "cs" | "de" | "en" | "es")
      : ("en" as const);

   // Mapy na importované SVG (CRA je zabalí a vrátí URL string)
   const googleBadges = { cs: gpCs, de: gpDe, en: gpEn, es: gpEs } as const;
   const appleBadges = { cs: iosCsBlack, de: iosDeBlack, en: iosEnBlack, es: iosEsBlack } as const;

   const googleBadgeSrc = googleBadges[lang];
   const appleBadgeSrc = appleBadges[lang];

   // Jednoduchý fallback: při chybě načtení přepnout na EN verzi
   const onGoogleBadgeError: React.ReactEventHandler<HTMLImageElement> = (e) => {
      const img = e.currentTarget;
      if (!img.dataset.fallback) {
         img.dataset.fallback = "1";
         img.src = googleBadges.en;
         console.warn("Google badge fallback → EN", { failed: googleBadgeSrc });
      }
   };

   const onAppleBadgeError: React.ReactEventHandler<HTMLImageElement> = (e) => {
      const img = e.currentTarget;
      if (!img.dataset.fallback) {
         img.dataset.fallback = "1";
         img.src = appleBadges.en;
         console.warn("Apple badge fallback → EN", { failed: appleBadgeSrc });
      }
   };

   // Měření šířky dvojice badge a fixace šířky group tak, aby
   // tlačítko „Open web app“ mělo přesně stejnou šířku.
   const groupRef = useRef<HTMLDivElement | null>(null);
   const rowRef = useRef<HTMLDivElement | null>(null);

   useEffect(() => {
     if (!groupRef.current || !rowRef.current) return;

     // Capture current elements to stable variables for effect + cleanup
     const groupEl = groupRef.current;
     const rowEl = rowRef.current;

     const measure = () => {
       if (groupEl && rowEl) {
         const w = Math.ceil(rowEl.getBoundingClientRect().width);
         if (w > 0) {
           groupEl.style.width = `${w}px`;
         }
       }
     };

     const RO: any = (window as any).ResizeObserver;
     const ro = RO ? new RO(() => measure()) : null;
     if (ro && rowEl) ro.observe(rowEl);
     window.addEventListener("resize", measure);
     const t = setTimeout(measure, 0);

     return () => {
       clearTimeout(t);
       window.removeEventListener("resize", measure);
       if (ro && rowEl) ro.unobserve(rowEl);
     };
   }, [lang]);

   return (
      <section id='download' className='download-section'>
         <Container>
            <Row className='align-items-center justify-content-center text-center h-100'>
               <Col md={6} className='text-center mb-4 mb-md-0'>
                  <img
                     src={appPreview}
                     alt='App Preview'
                     className='app-preview-image'
                  />
               </Col>
               <Col md={6} className='text-center'>
                  <h2>{t("download.title")}</h2>
                  <p>{t("download.description")}</p>
                  <div className='download-buttons'>
                    <div className='store-group' ref={groupRef}>
                      <div className='store-row' ref={rowRef}>
                        <a
                          href='https://play.google.com/store/apps/details?id=com.OctopodSystems.CheckChecker'
                          target='_blank'
                          rel='noopener noreferrer'
                          className='btn btn-google btn-lg'
                        >
                          <img src={googleBadgeSrc} alt='Google Play badge' className='store-badge' onError={onGoogleBadgeError} />
                        </a>
                        <a
                          href='https://apps.apple.com/app/checkchecker-by-octopodsystems/id6744112493'
                          target='_blank'
                          rel='noopener noreferrer'
                          className='btn btn-apple btn-lg'
                        >
                          <img src={appleBadgeSrc} alt='App Store badge' className='store-badge' onError={onAppleBadgeError} />
                        </a>
                      </div>
                      <a
                        href='https://app.checkchecker.app/app/signin'
                        target='_blank'
                        rel='noopener noreferrer'
                        className='btn btn-primary btn-lg'
                      >
                        <img src={webLogo} alt='Web App' className='btn-icon-image' />
                        <span className='btn-text'>
                          {t("webPromo.cta")}
                        </span>
                      </a>
                    </div>
                  </div>
               </Col>
            </Row>
         </Container>
      </section>
   );
};

export default Download;
